import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Home, Shield, Users, CheckCircle2, ArrowRight, TrendingUp, MapPin, Building2, Calculator, Percent, FileText, BookOpen, Mail, HelpCircle, ChevronDown } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import { trackEvent } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API}/${url.replace(/^\/+/, '')}`;
};

const formatPrice = (price) => {
  if (!price) return '';
  if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
  if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
  return `₹${price.toLocaleString('en-IN')}`;
};

const formatCityCount = (count) => {
  if (count >= 500) return '500+';
  if (count >= 100) return '100+';
  return String(count);
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchTab, setSearchTab] = useState('buy');
  const [searchCity, setSearchCity] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [showPriceDropdown, setShowPriceDropdown] = useState(false);
  const [emiLoan, setEmiLoan] = useState(5000000);
  const [emiRate, setEmiRate] = useState(8.5);
  const [emiTenure, setEmiTenure] = useState(20);
  const [cityCounts, setCityCounts] = useState({});
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [landingLoaded, setLandingLoaded] = useState(false);
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [builders, setBuilders] = useState([]);

  useEffect(() => {
    let retries = 0;
    const fetchLandingData = async () => {
      try {
        const [cityRes, projRes, faqRes, buildersRes] = await Promise.all([
          fetch(`${API}/api/listings/city-counts`),
          fetch(`${API}/api/listings/featured-projects`),
          fetch(`${API}/api/faqs`).catch(() => ({ ok: true, json: () => [] })),
          fetch(`${API}/api/builders`).catch(() => ({ ok: true, json: () => [] }))
        ]);
        const cityData = cityRes.ok ? await cityRes.json() : null;
        const projData = projRes.ok ? await projRes.json() : null;
        const faqData = faqRes.ok ? await faqRes.json() : [];
        const buildersData = buildersRes.ok ? await buildersRes.json() : [];
        if (cityData && Object.keys(cityData).length > 0) {
          setCityCounts(cityData);
        } else {
          throw new Error('Empty city data');
        }
        if (projData) {
          setFeaturedProjects(projData);
          projData.slice(0, 6).forEach(p => trackEvent('listing_impression', p.id));
        }
        setFaqs(faqData);
        setBuilders(buildersData);
        setLandingLoaded(true);
      } catch {
        if (retries < 3) {
          retries++;
          setTimeout(fetchLandingData, 1000 * retries);
        } else {
          setLandingLoaded(true);
        }
      }
    };
    fetchLandingData();
  }, []);

  const calculateEMI = () => {
    const r = emiRate / 12 / 100;
    const n = emiTenure * 12;
    if (r === 0) return emiLoan / n;
    return (emiLoan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  };

  const buyPriceOptions = [
    { value: '', label: 'No min' }, { value: '500000', label: '5 L' }, { value: '1000000', label: '10 L' },
    { value: '2000000', label: '20 L' }, { value: '3000000', label: '30 L' }, { value: '4000000', label: '40 L' },
    { value: '5000000', label: '50 L' }, { value: '6000000', label: '60 L' }, { value: '7000000', label: '70 L' },
    { value: '8000000', label: '80 L' }, { value: '9000000', label: '90 L' }, { value: '10000000', label: '1 Cr' },
    { value: '15000000', label: '1.5 Cr' }, { value: '20000000', label: '2 Cr' }, { value: '30000000', label: '3 Cr' },
    { value: '50000000', label: '5 Cr' }, { value: '100000000', label: '10 Cr' },
  ];
  const buyMaxOptions = [
    { value: '', label: 'No max' }, { value: '500000', label: '5 L' }, { value: '1000000', label: '10 L' },
    { value: '2000000', label: '20 L' }, { value: '3000000', label: '30 L' }, { value: '4000000', label: '40 L' },
    { value: '5000000', label: '50 L' }, { value: '6000000', label: '60 L' }, { value: '7000000', label: '70 L' },
    { value: '8000000', label: '80 L' }, { value: '9000000', label: '90 L' }, { value: '10000000', label: '1 Cr' },
    { value: '15000000', label: '1.5 Cr' }, { value: '20000000', label: '2 Cr' }, { value: '30000000', label: '3 Cr' },
    { value: '50000000', label: '5 Cr' }, { value: '100000000', label: '10 Cr' },
  ];
  const rentPriceOptions = [
    { value: '', label: 'No min' }, { value: '5000', label: '5K' }, { value: '10000', label: '10K' },
    { value: '15000', label: '15K' }, { value: '20000', label: '20K' }, { value: '25000', label: '25K' },
    { value: '30000', label: '30K' }, { value: '40000', label: '40K' }, { value: '50000', label: '50K' },
    { value: '75000', label: '75K' }, { value: '100000', label: '1 L' }, { value: '150000', label: '1.5 L' },
    { value: '200000', label: '2 L' },
  ];
  const rentMaxOptions = [
    { value: '', label: 'No max' }, { value: '5000', label: '5K' }, { value: '10000', label: '10K' },
    { value: '15000', label: '15K' }, { value: '20000', label: '20K' }, { value: '25000', label: '25K' },
    { value: '30000', label: '30K' }, { value: '40000', label: '40K' }, { value: '50000', label: '50K' },
    { value: '75000', label: '75K' }, { value: '100000', label: '1 L' }, { value: '150000', label: '1.5 L' },
    { value: '200000', label: '2 L' },
  ];
  const currentMinOptions = searchTab === 'rent' ? rentPriceOptions : buyPriceOptions;
  const currentMaxOptions = searchTab === 'rent' ? rentMaxOptions : buyMaxOptions;

  const getPriceLabel = (val, options) => {
    const opt = options.find(o => o.value === val);
    return opt ? opt.label : '';
  };
  const pricePillLabel = () => {
    if (!minPrice && !maxPrice) return 'Price';
    const minLabel = getPriceLabel(minPrice, currentMinOptions);
    const maxLabel = getPriceLabel(maxPrice, currentMaxOptions);
    if (minPrice && maxPrice) return `${minLabel} - ${maxLabel}`;
    if (minPrice) return `${minLabel}+`;
    return `Up to ${maxLabel}`;
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchTab === 'new_projects') {
      params.append('listing_type', 'buy');
      params.append('new_projects', 'true');
    } else {
      params.append('listing_type', searchTab);
    }
    if (searchCity) params.append('city', searchCity);
    if (propertyType) params.append('property_type', propertyType);
    if (bedrooms) params.append('bedrooms', bedrooms);
    if (minPrice) params.append('min_price', minPrice);
    if (maxPrice) params.append('max_price', maxPrice);
    navigate(`/properties?${params}`);
  };

  const popularCities = [
    { name: 'Trivandrum', image: 'https://customer-assets.emergentagent.com/job_637a2e66-b2b0-4ac1-80cd-4a374c4db381/artifacts/k5mvqx01_Screenshot%202026-03-29%20at%2012.24.57%E2%80%AFPM.png', imgStyle: { objectPosition: '25% center' } },
    { name: 'Kochi', image: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=400' },
    { name: 'Calicut', image: 'https://images.unsplash.com/photo-1693744440082-f286c85340b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxDYWxpY3V0JTIwS296aGlrb2RlJTIwYmVhY2glMjBLZXJhbGF8ZW58MHx8fHwxNzc0NzcyNTExfDA&ixlib=rb-4.1.0&q=85&w=600' },
    { name: 'Thrissur', image: 'https://images.pexels.com/photos/31376426/pexels-photo-31376426.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940' },
    { name: 'Palakkad', image: 'https://customer-assets.emergentagent.com/job_637a2e66-b2b0-4ac1-80cd-4a374c4db381/artifacts/4gawb03p_Screenshot%202026-03-29%20at%202.07.08%E2%80%AFPM.png' },
    { name: 'Kottayam', image: 'https://images.pexels.com/photos/15308719/pexels-photo-15308719.jpeg?auto=compress&cs=tinysrgb&w=600' },
  ];

  const exploreCards = [
    { icon: Shield, title: 'Verified Listings', desc: 'Browse authenticated properties validated by our team', link: '/browse' },
    { icon: TrendingUp, title: 'Price Trends', desc: 'Discover market insights and price movements', link: '/browse' },
    { icon: MapPin, title: 'Popular Areas', desc: 'Explore the most sought-after localities', link: '/browse' },
    { icon: Building2, title: 'Featured Builders', desc: 'Connect with trusted property developers', link: '/browse' },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "RealEstateAgent",
          "name": "PropTick",
          "description": "Discover verified premium properties across Kerala. Buy, rent, or invest in handpicked apartments, villas, lands & commercial spaces.",
          "url": "https://proptick.in",
          "areaServed": "Kerala, India",
          "serviceType": ["Real Estate Listings", "Property Discovery", "New Project Launches"]
        }}
      />
      <Navbar transparent />
      
      {/* Hero Section */}
      <section
        className="relative h-[720px] flex items-center justify-center bg-cover bg-center pt-16"
        style={{
          backgroundImage: 'linear-gradient(to bottom, rgba(15, 23, 42, 0.35), rgba(15, 23, 42, 0.75)), url(https://images.unsplash.com/photo-1766603636562-531bb3e1dda8?w=1920)'
        }}
      >
        <div className="relative z-10 text-center px-6 max-w-5xl w-full">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-tight text-white mb-4">
            Find Your Next Property
          </h1>
          <p className="text-lg sm:text-xl text-white/90 mb-8 sm:mb-12">
            Verified Premium Properties
          </p>
          
          {/* Search Box */}
          <div className="bg-white rounded-2xl shadow-2xl p-6" data-testid="hero-search-box">
            {/* Tabs */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setSearchTab('buy')}
                className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all ${
                  searchTab === 'buy' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                data-testid="tab-buy"
              >
                Buy
              </button>
              <button
                onClick={() => setSearchTab('rent')}
                className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all ${
                  searchTab === 'rent' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                data-testid="tab-rent"
              >
                Rent
              </button>
              <button
                onClick={() => setSearchTab('new_projects')}
                className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all ${
                  searchTab === 'new_projects' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                data-testid="tab-new-projects"
              >
                New Projects
              </button>
            </div>

            {/* Search Inputs - Single Row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <select
                value={searchCity}
                onChange={(e) => setSearchCity(e.target.value)}
                className="h-14 rounded-lg border-slate-300 bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-4 text-slate-700 font-medium"
                data-testid="search-city"
              >
                <option value="">Select City</option>
                {Object.keys(cityCounts).filter(c => c).sort().map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>

              <select
                value={propertyType}
                onChange={(e) => setPropertyType(e.target.value)}
                className="h-14 rounded-lg border-slate-300 bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-4 text-slate-700 font-medium"
                data-testid="search-property-type"
              >
                <option value="">Property Type</option>
                <option value="apartment">Apartment</option>
                <option value="villa">Villa</option>
                <option value="land">Land/Plot</option>
                <option value="commercial">Commercial</option>
              </select>

              <select
                value={bedrooms}
                onChange={(e) => setBedrooms(e.target.value)}
                className="h-14 rounded-lg border-slate-300 bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-4 text-slate-700 font-medium"
                data-testid="search-bedrooms"
              >
                <option value="">Bedrooms</option>
                <option value="1">1 BHK</option>
                <option value="2">2 BHK</option>
                <option value="3">3 BHK</option>
                <option value="4+">4+ BHK</option>
              </select>

              {/* Price Pill */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowPriceDropdown(!showPriceDropdown)}
                  className={`w-full h-14 rounded-lg border bg-slate-50 px-4 text-slate-700 font-medium flex items-center justify-between transition-all ${showPriceDropdown ? 'border-blue-500 ring-2 ring-blue-500' : 'border-slate-300'}`}
                  data-testid="search-price-pill"
                >
                  <span className={minPrice || maxPrice ? 'text-slate-900' : 'text-slate-500'}>{pricePillLabel()}</span>
                  <svg className={`w-4 h-4 text-slate-400 transition-transform ${showPriceDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>

                {showPriceDropdown && (
                  <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl z-30 p-4" data-testid="search-price-dropdown">
                    <p className="text-sm font-semibold text-slate-700 mb-3">Price Range</p>
                    <div className="flex items-center gap-2">
                      <select
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                        className="flex-1 h-12 rounded-lg border-slate-300 bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 text-sm text-slate-700 font-medium"
                        data-testid="search-min-price"
                      >
                        {currentMinOptions.map(opt => (
                          <option key={`min-${opt.value}`} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <span className="text-slate-400 font-medium">—</span>
                      <select
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                        className="flex-1 h-12 rounded-lg border-slate-300 bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 text-sm text-slate-700 font-medium"
                        data-testid="search-max-price"
                      >
                        {currentMaxOptions.map(opt => (
                          <option key={`max-${opt.value}`} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={handleSearch}
                className="h-14 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/30 flex items-center justify-center gap-2"
                data-testid="search-submit-button"
              >
                <Search className="w-5 h-5" />
                Search
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Handpicked Premium Homes */}
      {featuredProjects.length > 0 && (
      <section className="py-12 px-6 md:px-12 lg:px-24 bg-slate-50" data-testid="new-projects-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold mb-3">Handpicked Premium Homes</h2>
          <p className="text-lg text-slate-600 mb-10">Premium properties handpicked by our team</p>

          <div className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {featuredProjects.map((project) => (
              <div key={project.id} onClick={() => navigate(`/property/${project.slug || project.id}`)}
                className="group min-w-[300px] max-w-[300px] snap-start bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-all cursor-pointer flex-shrink-0"
                data-testid={`project-card-${project.id}`}>
                <div className="relative h-48 overflow-hidden">
                  <img src={resolveImg(project.images?.[0])} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                  <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-semibold text-white ${project.property_status === 'off_plan' ? 'bg-amber-500' : 'bg-blue-600'}`}>
                    {project.property_status === 'off_plan' ? 'New Launch' : 'Ready'}
                  </span>
                </div>
                <div className="p-5">
                  <h4 className="font-bold text-lg text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">{project.title}</h4>
                  <p className="text-sm text-slate-500 flex items-center gap-1 mb-3"><MapPin className="w-3.5 h-3.5" />{project.locality}, {project.city}</p>
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    {project.bedrooms && <span className="px-2.5 py-1 bg-slate-100 rounded-lg text-slate-700 font-medium">{project.bedrooms} BHK</span>}
                    {project.size_sqft && <span className="px-2.5 py-1 bg-slate-100 rounded-lg text-slate-700 font-medium">{project.size_sqft.toLocaleString('en-IN')} Sq.Ft</span>}
                    <span className="px-2.5 py-1 bg-blue-50 rounded-lg text-blue-700 font-semibold">{formatPrice(project.price)}{project.price_onwards && <span className="font-normal text-slate-400 ml-0.5">onwards</span>}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/new-projects')}
              className="h-12 px-8 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all inline-flex items-center gap-2"
              data-testid="explore-projects-btn"
            >
              Explore All Projects <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>
      )}

      {/* Popular Builders */}
      {builders.length > 0 && (
      <section className="py-10 px-6 md:px-12 lg:px-24 bg-white" data-testid="builders-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold mb-3">Popular Builders</h2>
          <p className="text-lg text-slate-600 mb-10">Trusted developers building Kerala's finest properties</p>
          <div className="flex gap-6 overflow-x-auto pb-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {builders.map((builder) => (
              <div
                key={builder.id}
                onClick={() => navigate(`/properties?search=${encodeURIComponent(builder.name)}&listing_type=buy`)}
                className="group min-w-[180px] max-w-[180px] flex-shrink-0 cursor-pointer"
                data-testid={`builder-card-${builder.id}`}
              >
                <div className="h-32 bg-white rounded-2xl border border-slate-200 flex items-center justify-center p-4 group-hover:border-blue-300 group-hover:shadow-lg transition-all">
                  <img src={resolveImg(builder.logo)} alt={builder.name} className="max-h-full max-w-full object-contain" />
                </div>
                <p className="text-sm font-semibold text-slate-900 mt-3 text-center">{builder.name}</p>
                {builder.project_count > 0 && (
                  <p className="text-xs text-slate-500 text-center">{builder.project_count} projects</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      <section className="py-12 px-6 md:px-12 lg:px-24 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold mb-3">Explore Popular Cities</h2>
          <p className="text-lg text-slate-600 mb-10">Find properties in Kerala's top real estate markets</p>
          
          <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {!landingLoaded ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-64 min-w-[240px] w-[240px] flex-shrink-0 rounded-2xl bg-slate-200 animate-pulse" />
              ))
            ) : popularCities.filter(city => cityCounts[city.name] > 0).map((city) => (
              <div
                key={city.name}
                onClick={() => {
                  navigate(`/properties-for-sale?city=${encodeURIComponent(city.name)}`);
                }}
                className="group relative h-64 min-w-[240px] w-[240px] flex-shrink-0 rounded-2xl overflow-hidden cursor-pointer"
                data-testid={`city-card-${city.name.toLowerCase()}`}
              >
                <img
                  src={city.image}
                  alt={city.name}
                  className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-500`}
                  style={city.imgStyle || {}}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent"></div>
                <div className="absolute bottom-6 left-6 text-white">
                  <h3 className="text-2xl font-bold mb-1">{city.name}</h3>
                  <p className="text-sm text-white/80">{formatCityCount(cityCounts[city.name] || 0)} properties</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 lg:px-24 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold mb-3">Enhance Your Search</h2>
          <p className="text-lg text-slate-600 mb-10">Explore tools and insights to find the perfect property</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {exploreCards.map((card) => (
              <div
                key={card.title}
                className="bg-white p-8 rounded-2xl border border-slate-200 hover:shadow-xl hover:border-blue-200 transition-all group"
                data-testid={`explore-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors">
                  <card.icon className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold mb-3">{card.title}</h3>
                <p className="text-slate-600">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EMI Calculator Section */}
      <section className="py-12 px-6 md:px-12 lg:px-24 bg-white" data-testid="emi-calculator-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-3">EMI Calculator</h2>
            <p className="text-lg text-slate-600">Plan your finances — estimate your monthly home loan EMI instantly</p>
          </div>

          <div className="bg-slate-50 rounded-3xl border border-slate-200 p-8 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {/* Inputs */}
              <div className="space-y-7">
                {/* Loan Amount */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-slate-700">Loan Amount</label>
                    <span className="text-sm font-bold text-blue-600" data-testid="emi-loan-display">
                      {emiLoan >= 10000000 ? `₹${(emiLoan / 10000000).toFixed(2)} Cr` : `₹${(emiLoan / 100000).toFixed(0)} L`}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="500000"
                    max="50000000"
                    step="100000"
                    value={emiLoan}
                    onChange={(e) => setEmiLoan(Number(e.target.value))}
                    className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer accent-blue-600"
                    data-testid="emi-loan-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>₹5 L</span>
                    <span>₹5 Cr</span>
                  </div>
                </div>

                {/* Interest Rate */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-slate-700">Interest Rate</label>
                    <span className="text-sm font-bold text-green-600" data-testid="emi-rate-display">{emiRate}%</span>
                  </div>
                  <input
                    type="range"
                    min="5"
                    max="15"
                    step="0.1"
                    value={emiRate}
                    onChange={(e) => setEmiRate(Number(e.target.value))}
                    className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer accent-green-500"
                    data-testid="emi-rate-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>5%</span>
                    <span>15%</span>
                  </div>
                </div>

                {/* Tenure */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-slate-700">Loan Tenure</label>
                    <span className="text-sm font-bold text-purple-600" data-testid="emi-tenure-display">{emiTenure} yrs</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    step="1"
                    value={emiTenure}
                    onChange={(e) => setEmiTenure(Number(e.target.value))}
                    className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer accent-purple-500"
                    data-testid="emi-tenure-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>1 yr</span>
                    <span>30 yrs</span>
                  </div>
                </div>
              </div>

              {/* Result */}
              <div className="flex flex-col justify-center">
                <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
                  <div className="bg-blue-600 text-white rounded-xl p-5 text-center">
                    <p className="text-blue-200 text-sm mb-1">Monthly EMI</p>
                    <p className="text-4xl font-bold" data-testid="emi-result">
                      ₹{Math.round(calculateEMI()).toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-500">Total Interest</p>
                      <p className="font-bold text-slate-800 text-sm" data-testid="emi-total-interest">
                        ₹{Math.round(calculateEMI() * emiTenure * 12 - emiLoan).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-500">Total Payment</p>
                      <p className="font-bold text-slate-800 text-sm" data-testid="emi-total-payment">
                        ₹{Math.round(calculateEMI() * emiTenure * 12).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose PROP<span className="font-normal">TICK</span></h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Kerala's verified premium property platform with trusted listings and direct connections
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-slate-50 rounded-2xl border border-slate-100 hover:bg-white hover:shadow-lg transition-all" data-testid="feature-verified">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-4">100% Verified</h3>
              <p className="text-slate-600">
                Every property listing is verified by our expert team for authenticity
              </p>
            </div>

            <div className="text-center p-8 bg-slate-50 rounded-2xl border border-slate-100 hover:bg-white hover:shadow-lg transition-all" data-testid="feature-direct">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Direct Connect</h3>
              <p className="text-slate-600">
                Chat directly with property owners and builders without intermediaries
              </p>
            </div>

            <div className="text-center p-8 bg-slate-50 rounded-2xl border border-slate-100 hover:bg-white hover:shadow-lg transition-all" data-testid="feature-transparent">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-4">No Hidden Fees</h3>
              <p className="text-slate-600">
                Transparent pricing with no hidden charges for buyers
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/proptick-logo.png" alt="PropTick" className="h-12 brightness-0 invert" />
              </div>
              <p className="text-slate-400 text-sm">Verified Premium Properties</p>
              <a
                href="/list-property"
                onClick={(e) => { e.preventDefault(); navigate('/list-property'); }}
                className="inline-flex items-center gap-2 mt-5 h-10 px-5 rounded-full border border-slate-600 text-sm font-medium text-white hover:bg-white hover:text-slate-900 transition-colors"
                data-testid="footer-list-property-link"
              >
                <Home className="w-4 h-4" /> List Property
              </a>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Popular Cities</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                {Object.keys(cityCounts).filter(c => c && cityCounts[c] > 0).sort().map(city => (
                  <li key={city}><a href={`/properties-for-sale?city=${encodeURIComponent(city)}`} className="hover:text-white transition-colors">Properties in {city}</a></li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Property Types</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="/properties-for-sale?property_type=apartment" className="hover:text-white transition-colors">Apartments for Sale</a></li>
                <li><a href="/properties-for-sale?property_type=villa" className="hover:text-white transition-colors">Villas for Sale</a></li>
                <li><a href="/properties-for-sale?property_type=land" className="hover:text-white transition-colors">Land & Plots</a></li>
                <li><a href="/properties-for-sale?property_type=commercial" className="hover:text-white transition-colors">Commercial</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="/about-us" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/terms-and-conditions" className="hover:text-white transition-colors">Terms & Conditions</a></li>
                <li><a href="/delete-account" className="hover:text-white transition-colors">Delete Account</a></li>
                <li><a href="/faqs" className="hover:text-white transition-colors">FAQs</a></li>
              </ul>
              
              <h4 className="font-semibold mb-4 mt-8">Blog</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="/blog" className="hover:text-white transition-colors flex items-center gap-2" data-testid="footer-blog-link"><BookOpen className="w-4 h-4" /> Blog</a></li>
              </ul>

              <h4 className="font-semibold mb-4 mt-8">Contact Us</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <a href="mailto:admin@proptick.in" className="hover:text-white transition-colors flex items-center gap-2" data-testid="footer-contact-email">
                    <Mail className="w-4 h-4" /> admin@proptick.in
                  </a>
                </li>
                <li>
                  <a href="https://www.instagram.com/proptick.in/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center gap-2" data-testid="footer-instagram-link">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                    @proptick.in
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 text-center text-sm text-slate-400">
            <p>&copy; 2026 Proptick. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
